package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/state_hosting")
@Composable
fun ComposeStateHostingPage() {
    BlogLayout(
        PageProperties(
            "State Hosting",
            "State hoisting means that the parent owns the state, and the child only displays it and notifies when it changes. This promotes a more predictable, scalable architecture, reusability, testing, and state control."
        ),
        BlogProperties("Jetpack Compose", "State Hosting")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State hoisting is a pattern where you move state from a composable function to its caller, making the composable stateless. The composable receives state as parameters and notifies changes through callback functions. (The state owner passes the value down as a parameter and a callback (lambda) up for the child to request changes). State hoisting follows the principles of unidirectional data flow, making the UI easier to manage and more scalable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State hoisting means moving state from a composable to its caller. Instead of a composable managing its own state internally, it receives state as a parameter and notifies the caller when that state should change.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State should live in the lowest possible level that still owns the business decision.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Too low → duplicated logic",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Too high → unnecessary recompositions",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State hoisting is a pattern where a composable does not store its own state, but instead receives state and a state update function from its parent.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Imagine a bakery with multiple kitchens. You have a main recipe, the parent composable, for delicious cookies. This recipe calls for steps like mixing dry ingredients, creaming butter and sugar, and baking. The steps are the child composables. Instead of each step having a separate stock of ingredients (state), what if you could keep a central pantry for the entire bakery, the Compose hierarchy? The central pantry would be located at a higher level in the bakery and accessible to all steps that need the ingredients.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State hoisting means that the parent owns the state, and the child only displays it and notifies when it changes. This promotes a more predictable and scalable architecture.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In simple terms, state hoisting is the process of moving state up from a child composable into its parent. Instead of a UI component directly owning and mutating its state, the parent holds the state and passes it down, while the child only receives data and exposes events.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State hoisting is a pattern in Jetpack Compose that involves moving UI state up the composable hierarchy to the lowest common ancestor (LCA). This common ancestor then manages and controls the state, making it accessible to all its child composables that need it. It involves moving state up the composition tree to a common ancestor composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This is achieved by creating state in a composable function and passing it down to child composables through function parameters. By passing the state down as function parameters, state hoisting decouples the state from the UI elements, leading to improved code modularity, reusability, testability, and maintainability.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When we extract state from a composable function, which is stateful before and after extracting, the composable function is now stateless. That is, composable functions can be made stateless by extracting state from them. This pattern is called state hoisting.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Compose, the state that is read or modified by multiple composables should live in a common parent, this process is called state hoisting.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State hoisting is a technique in Jetpack Compose that involves moving the state to a higher-level component to facilitate sharing and synchronization across multiple child components.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Ensuring composables remain stateless, improving testability and reusability.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State hoisting in Jetpack Compose is a pattern of moving state to a composable caller to make a composable stateless.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Whenever our state is inside any function so its scope is only defined inside that block, it can't be used outside of the block. so to access that state we need to make it global so we can use it anywhere we want.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "After making the state ready to share with other functions now the function that was getting the state is now stateless.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                    //count: Current value to display
                    //onIncrement: An event that request current value to change
                    
                    @Composable
                    fun Counter(count: Int, onIncrement: () -> Unit) {
                        Column {
                            Text(text = count)
                            Button(onClick = { onIncrement() }) {
                                Text(text = "Increment")
                            }
                        }
                    }

                    @Composable
                    fun CounterScreen() {
                        var count by remember { mutableStateOf(0) } //Manage parent state

                        Counter(count = count, onIncrement = { count++ })
                    }
                    """.trimIndent()
                )

                KotlinCode(
                    code = $$"""
                        @Composable
                        fun StatelessCounter(count: Int, onIncrement: () -> Unit) {
                            Button(onClick = onIncrement) {
                                Text("Count: $count")
                            }
                        }

                        @Composable
                        fun StatefulCounter() {
                            var count by remember { mutableStateOf(0) }
                            StatelessCounter(count, onIncrement = { count++ })
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                        //State is managed in the parent composable.
                        //Events or triggers (like onClick, onValueChange) are passed from the child back to the parent, which updates the state.
                        //The updated state is then passed back down as parameters to the child, creating a unidirectional data flow.
                        
                        @Composable
                        fun ParentComponent() {
                            var text by remember { mutableStateOf("") } //Manage parent state

                            ChildComponent(text) {
                                text = it
                            }
                        }

                        @Composable
                        fun ChildComponent(text: String, onTextChange: (String) -> Unit) {
                            TextField(value = text, onValueChange = onTextChange)
                        }
                    """.trimIndent()
                )

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In this example, the text state is hoisted to the ParentComponent, and it is passed down to the ChildComponent as a parameter. The ChildComponent receives the text value and a callback onTextChange to update the value. This way, the ParentComponent maintains control over the state, and any changes made in the ChildComponent are propagated back to the parent.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By hoisting the state, we can easily share it between multiple components, orchestrate interactions, and maintain a single source of truth for the data.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun HomeScreen() {
                        Column(modifier = Modifier.fillMaxSize().background(Color.White),
                            verticalArrangement = Arrangement.Center,
                            horizontalAlignment = Alignment.CenterHorizontally) {

                            val output = remember { mutableIntStateOf(0) }

                            Text(text = output.intValue.toString() )

                            ButtonExample(value = output.intValue) {
                                    increment ->
                                output.intValue = increment
                            }
                        }
                    }

                    @Composable
                    fun ButtonExample(value: Int, click:(increment:Int) -> Unit) {
                        Button(onClick = { click(value+1)}) {
                            Text(text = "Click Me")
                        }
                    }
            """.trimIndent())

            SpanText(
                text = "When to use this pattern",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To create reusable Stateless component When state need to be shared. Multiple components need same state. Parent controls the child behaviour. State need to be saved/ restored.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Safer pattern",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //Tips Smart Work
            KotlinCode(
                code = """
                    //Compose uses referential equality to decide recomposition.
                    //New lambda = potential recomposition.
                    
                    //1
                    val onUserClick: (String) -> Unit = remember {
                        { id -> viewModel.onUserClick(id) }
                    }  // ❌ captures old VM

                    items(users) { user ->
                        UserItem(
                            user = user,
                            onClick = { onUserClick(user.id) }
                        )
                    }
                    
                    //2. Correct pattern if viewModel may change
                    val latestVm by rememberUpdatedState(viewModel)

                    val onUserClick: (String) -> Unit = remember {
                        { id -> latestVm.onUserClick(id) }
                    }
                    
                    //3.
                    val onUserClick: (String) -> Unit = viewModel::onUserClick
                    """.trimIndent()
            )

            SpanText(
                text = "When to Use State Hoisting",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A child composable needs to communicate with a parent (State is managed at the appropriate level).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You want to make composables more reusable and testable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Managing complex state directly in a composable becomes cumbersome.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It integrates easily with ViewModels or shared state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    @Composable
                    fun ParentComposable() {
                        // count and onIncrement are managed by ParentComposable
                        var count by remember { mutableStateOf(0) }
                        CounterButton(count = count, onIncrement = { count++ })
                    }

                    // CounterButton becomes a stateless, reusable composable 
                    // that simply displays the count and triggers the onIncrement 
                    // function when clicked
                    @Composable
                    fun CounterButton(count: Int, onIncrement: () -> Unit) {
                        Button(onClick = onIncrement) {
                            Text("Clicked $count times")
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Locale State",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This state is owned by a single composable and not shared outside of it. It’s typically used for UI elements that don’t need to share their state with other parts of the UI. Local state is created using remember and mutableStateOf, which retain values across recompositions within the same composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Local state is the state managed within a single composable function.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For example, a toggle button that expands or collapses a section of text typically uses local state because it’s used only within that composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Local state management is straightforward.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You often use the mutableStateOf function, which tells Compose to observe this state for changes and redraw the composable whenever it updates.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Logic can be reused.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Only the composable itself uses it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Component has simpler API.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It’s a transient UI state (animations, expansions).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hoisting would add unnecessary complexity.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun RememberMeExample() {
                        var rememberMe by remember { mutableStateOf(false) }
                        Checkbox(
                            checked = rememberMe,
                            onCheckedChange = { rememberMe = it }
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Hoist/Hoisted State (Shared State)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In this pattern, the state is moved up to a shared parent component, making it easier to manage across different parts of the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hoisted state is useful when multiple composables need access to the same state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun ParentComposable() {
                        var sharedCount by remember { mutableStateOf(0) } // Shared state
                        ChildComposable(sharedCount, onClick = { sharedCount++ })
                    }

                    @Composable
                    fun ChildComposable(count: Int, onClick: () -> Unit) {
                        Button(onClick = onClick) {
                            Text("Shared count: $\count")
                        }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In a Jetpack Compose application, the decision of where to hoist UI state depends on whether it’s primarily used for UI logic or business logic. This article explores these two main scenarios and the benefits of hoisting state effectively. Before we dive into hoisting state, let’s understand the some terms UI State, UI Logic and Business Logic:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "UI State: This refers to the data that controls the visual appearance of the UI elements, such as the text displayed in a button or the selected item in a dropdown menu. Currently selected category (dropdown selection) and search query (text input).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "UI Logic: This refers to how UI state is presented on the screen. Examples include determining UI element behavior based on user interaction (like showing/hiding a button) or updating visuals based on other UI state changes.  For example, a UI element can be shown or hidden, have a specific font, font size, or font color.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Business Logic: This involves implementing core functionalities of your app that handle data manipulation and business rules. An example is saving user preferences or interacting with a database.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    //State Hoisting for UI Logic
                    // ProductFilter.kt
                    @Composable
                    fun ProductFilter(
                        selectedCategory: String,
                        onCategorySelected: (String) -> Unit,
                        searchQuery: String,
                        onSearchQueryChanged: (String) -> Unit
                    ) {
                        // UI elements for category dropdown and search bar
                        DropdownMenu(selectedCategory, onCategorySelected)
                        SearchBar(searchQuery, onSearchQueryChanged)
                    }

                    //State Hoisting for Business Logic
                    // ProductFilterScreen.kt
                    @Composable
                    fun ProductFilterScreen(viewModel: ProductFilterViewModel = viewModel()) {

                        var selectedCategory by remember { mutableStateOf("") }
                        var searchQuery by remember { mutableStateOf("") }

                        ProductFilter(
                            selectedCategory = selectedCategory,
                            onCategorySelected = { category -> selectedCategory = category },
                            searchQuery = searchQuery,
                            onSearchQueryChanged = { query -> searchQuery = query }
                        )
                    }

                    // ProductFilterViewModel.kt
                    class ProductFilterViewModel : ViewModel() {

                        private val _selectedCategory = mutableStateOf("")
                        val selectedCategory: State<String> = _selectedCategory

                        private val _searchQuery = mutableStateOf("")
                        val searchQuery: State<String> = _searchQuery

                        fun setSelectedCategory(category: String) {
                            _selectedCategory.value = category
                        }

                        fun setSearchQuery(query: String) {
                            _searchQuery.value = query
                        }

                        fun saveFilterSettings() {
                            // Save filter settings logic here
                        }
                    }


                    // ProductFilter.kt
                    @Composable
                    fun ProductFilter(viewModel: ProductFilterViewModel) {
                        // UI elements for category dropdown and search bar
                        DropdownMenu(
                            selectedCategory = viewModel.selectedCategory.value,
                            onCategorySelected = viewModel::setSelectedCategory
                        )
                        SearchBar(
                            searchQuery = viewModel.searchQuery.value,
                            onSearchQueryChanged = viewModel::setSearchQuery
                        )

                        Button(onClick = viewModel::saveFilterSettings) {
                            Text("Save Filter")
                        }
                    }


                    // ProductFilterScreen.kt
                    @Composable
                    fun ProductFilterScreen(viewModel: ProductFilterViewModel = viewModel()) {
                        ProductFilter(viewModel = viewModel)
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //https://www.youtube.com/watch?v=PMMY23F0CFg

                    //Way 1
                    @Composable
                    fun WellnessScreen(modifier: Modifier = Modifier) {
                        Column (modifier = modifier) {
                            val list = remember { getWellnessTasks().toMutableStateList() }

                            WellnessTasksList(list = list, onCloseTask = {})
                        }
                    }

                    private fun getWellnessTasks() = List ( size: 30) { i -> WellnessTask(i, label: "Task $i") }

                    //Way 2
                    @Composable
                    fun WellnessTasksList(
                        List: List<WellnessTask>,
                        onCloseTask: (WellnessTask) -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        LazyColumn (
                            modifier = modifier
                        ) {
                            items (items = list, key = { task -> task.id }) { task ->
                                WellnessTaskItem(taskName = task.label, onClose = { onCloseTask(task) })
                            }
                        }
                    }

                    data class WellnessTask(val id : Int, val label: String)

                    @Composable
                        fun WellnessTaskItem(taskName: String, onClose: () -> Unit, modifier: Modifier = Modifier) {
                        var checkedState by remember { mutableState0f( value: false) }

                        WellnessTaskItem(
                            taskName = taskName,
                            checked = checkedState,
                            onCheckedChange = { newValue -> checkedState = newValue },
                            //onClose = {}, // we will implement this later!
                            onClose = onClose,
                            modifier = modifier,
                        )
                    }

                    @Composable
                    fun WellnessTaskItem(
                        taskName: String,
                        checked: Boolean,
                        onCheckedChange: (Boolean) -> Unit,
                        onClose: () -> Unit,
                        modifier: Modifier = Modifier)
                        {
                            Checkbox(
                                checked = checked,
                                onCheckedChange = onCheckedChange
                            )
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //State Sharing Multiple composables
                    
                    @Composable
                    fun SearchScreen() {
                        var query by remember { mutableStateOf("") }
                        
                        Column {
                            SearchBar(
                                query = query,
                                onQueryChange = { query = it }
                            )
                            
                            // Both components use the same state
                            SearchSuggestions(query = query)
                            SearchResults(query = query)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Hoist state when",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Multiple composables need to read or modify it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The state needs to survive recomposition in a parent.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "You want to test the composable’s behavior.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Different contexts need different state sources",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Why state hoisting",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Reusability: By hoisting state, you decouple the composable from the specific state management. This makes the composable more reusable in different contexts with different state sources.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Testability: Composables with hoisted state are easier to test because you can directly control the state passed to them.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Single Source of Truth: Hoisting state ensures that there's a single source of truth for the state, preventing inconsistencies and making it easier to reason about how your UI behaves.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When use state hoisting",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Share the state with multiple composable functions",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Create a stateless composable",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Centralizing state management outside of UI components",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "When use Hoist State",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "There are more (or more complex) parameters.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Logic can be more easily customised.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Caller can decide how state is stored and how persistent is it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We can easily preview all different states.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "How does state hoisting work in Compose and why is it important?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Instead of managing state internally within a composable, you pass the state down as a parameter and a lambda function to update the state. The parent composable is then responsible for holding and managing the state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State hoisting is a design pattern in Jetpack Compose where state is moved out of a composable, making the composable stateless and reusable. Instead of managing its own internal state, the composable receives state and a callback to update it. (usually following the value and onValueChange pattern).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In short, state hoisting promotes clean architecture, testability, and reusability — all essential qualities in modern, declarative UI design.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "No state hoisting needed",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Hoisting state isn't always required. State can be kept internal in a composable when no other composable need to control it. In this snippet, there is a composable that expands and collapses on tap:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    @Composable
                    fun ChatBubble(
                        message: Message
                    ) {
                        var showDetails by rememberSaveable { mutableStateOf(false) } // Define the UI element expanded state

                        ClickableText(
                            text = AnnotatedString(message.content),
                            onClick = { showDetails = !showDetails } // Apply simple UI logic
                        )

                        if (showDetails) {
                            Text(message.timestamp)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "The state should be hoisted to at least the lowest common parent of all composables that use the state (read)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This means that the state should be moved to a level in the composable hierarchy where all the composables that need to access or read the state can reach it. By hoisting the state to this common parent, it ensures that all relevant composables have access to the state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "The state should be hoisted to at least the highest level at which it may be changed (write)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This rule suggests that the state should be moved to a level where it can be modified or changed by the relevant composable. By hoisting the state to this highest level, it ensures that the necessary composables have the ability to update the state when needed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "If two states change in response to the same events, they should be hoisted together",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If multiple states are modified or updated in response to the same events or actions, it is recommended to hoist them together. By hoisting them together, it maintains the relationship between these states and ensures that they are managed consistently when changes occur.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Hosting",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composable's that use remember or rememberSavable to store objects contains internal state. These Composable's are called stateful. These Composable's are hard to reuse and harder to test. This is where hoisting helps.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State hoisting in Compose is a pattern of moving state to a composable's caller to make a composable stateless.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                SpanText(
                    text = "The general pattern for state hoisting in Jetpack Compose is to replace the state variable with two parameters:",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )

                Ol {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "value: T - The current value to display",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "onValueChange: (T) -> Unit an event that requests the value to change, where T is the proposed new value. Where this value represents any state that could be modified.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Type of state hoisting",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                SpanText(
                    text = "Stateless",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Receive state & events from outside (flexible, testable, and work great with ViewModels).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Stateless composable is a composable that doesn't own any state, meaning it doesn't hold or define or modify new state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Composable that does not hold a state in itself and uses state hoisting to pass any state as an argument to a composable function is considered stateless composable",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "They rely on external data, reducing unnecessary recompositions since they only update when these data change.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This also significant performance benefits, enhances reusability and testability, making the code cleaner and easier to maintain.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = $$"""
                        //Without Hoisting

                        @Composable
                        fun Counter() {
                            var count by remember { mutableStateOf(0) }
                            Column {
                                Text("Count: $count")
                                Button(onClick = { count++ }) {
                                    Text("Increment")
                                }
                            }
                        }
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                    //wrong way
                    @Composable
                    fun MyOwnButtonWrong(item: String) {
                      //managing state inside the composable - WRONG
                      val buttonState = remember { mutableStateOf("Not Clicked!") }
                        Button(onClick = { buttonState.value = "clicked!" }) {
                            Text(text = buttonState.value)
                        }
                    }

                    //Correct way
                    @Composable
                    fun MyOwnButton(item: String, onClick: () -> Unit) {
                        Button(onClick = onClick) {
                            Text(text = item)
                        }
                    }

                    //usage
                    @Composable
                    fun HomeScreen() {
                        val buttonState = remember { mutableStateOf("Not Clicked!") }
                        MyOwnButton(
                          item = buttonState.value,
                          onClick = { buttonState.value = "Clicked!" }
                        )
                    }
                    """.trimIndent()
                )

                SpanText(
                    text = "Stateful",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Manage their own state (simple, but less flexible.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Stateful composables that use remember to store data internally. This is useful when the caller does not need to control the state. However, Composables with internal state tend to be less reusable and more difficult to test.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Stateful composable is a composable that owns a piece of state that can change over time.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Composable that has its own state and manages that state within itself is considered Stateful Composable.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                KotlinCode(
                    code = """
                    //stateful
                    @Composable
                    fun Greeting() {
                        // `name` is a state variable that holds the current value of the input field
                        var name by remember { mutableStateOf("") }
                        Column {
                            TextField(
                                value = name,
                                onValueChange = { newName -> name = newName },
                                label = { Text("Enter your name") }
                            )
                            Text(text = name!)
                        }
                    }

                    //stateless
                    @Composable
                    fun Greeting(name: String, onNameChange: (String) -> Unit) {
                        Column {
                            TextField(
                                value = name,
                                onValueChange = onNameChange,
                                label = { Text("Enter your name") }
                            )
                            Text(text = name!)
                        }
                    }
                    """.trimIndent()
                )
            }

            SpanText(
                text = "State Sharing",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    fun SearchScreen() {
                        var query by remember { mutableStateOf("") }
                        
                        Column {
                            SearchBar(
                                query = query,
                                onQueryChange = { query = it }
                            )
                            
                            // Both components use the same state
                            SearchSuggestions(query = query)
                            SearchResults(query = query)
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "mutable and immutable objects",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Indicates a type where the value of any properties will never change after the object is constructed, and all methods are referentially transparent. All primitive types (String, Int, Float, etc) are considered immutable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                KotlinCode(
                    code = """
                        //immutable
                        data class Contact(val name: String, val number: String)
                    """.trimIndent()
                )

                KotlinCode(
                    code = """
                        //mutable
                        data class Contact(var name: String, var number: String)
                    """.trimIndent()
                )
            }

            SpanText(
                text = "How to make a variable observable?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State, MutableState and mutableStateListOf type makes the variable observable by Compose.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Compose keeps track of each composable that reads this value and schedules a recomposition when the variable state changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                        // mutableListOf makes count observable
                        var count : MutableState<Int> = mutableListOf(0)
                        
                        var currentPage by mutableStateOf(0)
                            private set

                        // Current screen state
                        var currentScreen: MutableState<String> = mutableStateOf(startDestination)
                            private set
                            
                    """.trimIndent()
            )

            SpanText(
                text = "How does State differ from a regular variable in Compose?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State automatically triggers recomposition, while a normal variable does not. When a State value changes, only the Composables that read it are recomposed. A regular variable (var x = 0) doesn’t notify the UI, so updates won’t be reflected on screen.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                SpanText(
                    text = "Remembering the state variable value",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When you use a state variable inside a composable function (a Ul function in Jetpack Compose), its value should be remembered between function calls. This means that the state doesn't get reset every time the composable function is called again.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "If it were a normal variable, it would get reset each time the function is called, but for state variables, Jetpack Compose makes sure they \"remember\" their previous values so the Ul stays consistent.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                SpanText(
                    text = "Implications of changing a state variable",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "When you change the value of a state variable, it triggers a rebuild of not just the composable function where the state is defined, but also all the composable functions that depend on that state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "This means that the entire Ul tree (the hierarchy of composables) can be affected, and parts of it may need to be redrawn to reflect the new state. This is different from a regular function where changes in local variables only affect that specific function.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    //State variable that remembers its value
                    //count is a state variable. Its value is "remembered" across recompositions. When count changes (on clicking the button), the Ul updates automatically.
                    var count by remember { mutableState0f(0) }
                    
                    //Regular Kotlin variable
                    //When the button is clicked, count is incremented, but it doesn't "remember" its previous value.
                    //As a result, the composable doesn't re-render with the updated value because the value of count is reset every time the composable is called.
                    var count = 0
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // ❌ JUNIOR: State inside the composable — can't reuse, can't test, can't preview
                    @Composable
                    fun AmountInput() {
                        var amount by remember { mutableStateOf("") }
                        var isValid by remember { mutableStateOf(true) }

                    TextField(
                            value = amount,
                            onValueChange = {
                                amount = it
                                isValid = it.toDoubleOrNull() != null && it.toDouble() > 0
                            },
                            isError = !isValid,
                            label = { Text("Amount") }
                        )
                    }
                    
                    // Problems:
                    // - Can't read the amount from outside (parent doesn't know the value)
                    // - Can't set initial amount (e.g., editing an existing transfer)
                    // - Can't validate with external rules (e.g., max balance)
                    // - Can't test without running Compose
                    // ✅ SENIOR: State hoisted to caller - composable is stateless
                    @Composable
                    fun AmountInput(
                        amount: String,                    // State passed DOWN (from parent)
                        onAmountChange: (String) -> Unit,  // Events passed UP (to parent)
                        isError: Boolean = false,
                        errorMessage: String? = null,
                        modifier: Modifier = Modifier
                    ) {
                        Column(modifier = modifier) {
                            TextField(
                                value = amount,
                                onValueChange = onAmountChange,
                                isError = isError,
                                label = { Text("Amount") },
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                                singleLine = true
                            )
                            if (isError && errorMessage != null) {
                                Text(
                                    errorMessage,
                                    color = MaterialTheme.colorScheme.error,
                                    style = MaterialTheme.typography.bodySmall,
                                    modifier = Modifier.padding(start = 16.dp, top = 4.dp)
                                )
                            }
                        }
                    }
                    
                    // Parent controls ALL state:
                    @Composable
                    fun CreateTransferScreen(viewModel: CreateTransferViewModel = hiltViewModel()) {
                        val state by viewModel.state.collectAsStateWithLifecycle()
                        AmountInput(
                            amount = state.amount,
                            onAmountChange = viewModel::onAmountChanged,
                            isError = state.amountError != null,
                            errorMessage = state.amountError
                        )
                    }
                    
                    // NOW the component is: reusable, testable, previewable
                    @Preview
                    @Composable
                    fun AmountInputPreview() {
                        AmountInput(amount = "500.00", onAmountChange = {}, isError = false)
                    }
                    
                    @Preview
                    @Composable
                    fun AmountInputErrorPreview() {
                        AmountInput(amount = "0", onAmountChange = {}, isError = true, errorMessage = "Amount must be greater than 0")
                    }
                    """.trimIndent()
            )
        }
    }
}

//https://syedovaiss.medium.com/state-hoisting-in-jetpack-compose-why-your-ui-needs-a-viewmodel-20b85951c42b